export type ClubDocumentAudience = "admins" | "members" | "club";
export type ClubDocumentAcceptance = "none" | "new_members" | "all_current_members";

/**
 * Maps the old members_must_accept booleans onto audience and acceptance.
 * Keep this aligned with public.club_document_access_from_flags.
 */
export function clubDocumentAccessFromLegacyFlags(
  mustAccept: boolean,
  mustAcceptThisVersion: boolean,
): { audience: ClubDocumentAudience; acceptance: ClubDocumentAcceptance } {
  if (mustAcceptThisVersion) {
    return { audience: "members", acceptance: "all_current_members" };
  }
  if (mustAccept) {
    return { audience: "members", acceptance: "new_members" };
  }
  return { audience: "club", acceptance: "none" };
}

/** Inverse of clubDocumentAccessFromLegacyFlags, for the existing document screens. */
export function legacyDocumentFlags(acceptance: ClubDocumentAcceptance | null | undefined): {
  membersMustAccept: boolean;
  membersMustAcceptThisVersion: boolean;
} {
  return {
    membersMustAccept: acceptance != null && acceptance !== "none",
    membersMustAcceptThisVersion: acceptance === "all_current_members",
  };
}

export type LegacyAcceptanceChoice = "all" | "new" | "none";

export function legacyChoiceFromAcceptance(
  acceptance: ClubDocumentAcceptance | null | undefined,
): LegacyAcceptanceChoice {
  if (acceptance === "all_current_members") return "all";
  if (acceptance === "new_members") return "new";
  return "none";
}

export function accessFromLegacyChoice(choice: LegacyAcceptanceChoice): {
  audience: ClubDocumentAudience;
  acceptance: ClubDocumentAcceptance;
} {
  if (choice === "all") return clubDocumentAccessFromLegacyFlags(true, true);
  if (choice === "new") return clubDocumentAccessFromLegacyFlags(true, false);
  return clubDocumentAccessFromLegacyFlags(false, false);
}

export function audienceLabel(audience: ClubDocumentAudience | null | undefined): string {
  if (audience === "admins") return "Admins";
  if (audience === "members") return "Members";
  return "Whole club";
}

export function acceptanceLabel(acceptance: ClubDocumentAcceptance | null | undefined): string {
  if (acceptance === "all_current_members") return "All current members";
  if (acceptance === "new_members") return "New members";
  return "None";
}

export function acceptanceSourceLabel(source: "self" | "admin" | null | undefined): string {
  return source === "admin" ? "Staff" : "Member";
}

type DocumentAccess = {
  audience?: ClubDocumentAudience | null;
  acceptance?: ClubDocumentAcceptance | null;
  is_deleted?: boolean | null;
};

/** Latest members document that every open membership still needs to accept. */
export function documentChasesCurrentMembers(document: DocumentAccess): boolean {
  return (
    !document.is_deleted && document.audience === "members" && document.acceptance === "all_current_members"
  );
}

/** Latest members document that must be accepted before a membership starts. */
export function documentRequiredBeforeMembership(document: DocumentAccess): boolean {
  return (
    !document.is_deleted &&
    document.audience === "members" &&
    document.acceptance != null &&
    document.acceptance !== "none"
  );
}

type AcceptanceIdentity = {
  is_deleted?: boolean | null;
  version?: number | null;
  series_id?: string | null;
  document_name?: string | null;
};

type DocumentIdentity = {
  version?: number | null;
  series_id?: string | null;
  name?: string | null;
};

function sameSeries(acceptance: AcceptanceIdentity, document: DocumentIdentity): boolean {
  if (acceptance.series_id && document.series_id) return acceptance.series_id === document.series_id;
  return acceptance.document_name != null && acceptance.document_name === document.name;
}

/** Same series and version, including a row that was later withdrawn. */
export function acceptanceSameVersion(acceptance: AcceptanceIdentity, document: DocumentIdentity): boolean {
  return acceptance.version === document.version && sameSeries(acceptance, document);
}

/** Live acceptance of this series, any version. */
export function acceptanceMatchesSeries(acceptance: AcceptanceIdentity, document: DocumentIdentity): boolean {
  return !acceptance.is_deleted && sameSeries(acceptance, document);
}

/** Live acceptance of this exact version. Source does not matter. */
export function acceptanceCoversDocument(
  acceptance: AcceptanceIdentity,
  document: DocumentIdentity,
): boolean {
  return !acceptance.is_deleted && acceptanceSameVersion(acceptance, document);
}
